/**
 * Workspace Secret v2 types and utilities.
 * Spec §4.1: replaces the legacy capability format.
 *
 * Canonical shapes MUST be identical across JS SDK, C# SDK, webapp,
 * sc-viewer, MCP, FUSE, SmartRunner and backend.
 */

// ── V2 format types ────────────────────────────────────────────

/**
 * Workspace Secret v2 — client-local secret containing readKey,
 * readerSeed and optional writerSeed.
 * Spec §4.1.
 */
export interface WorkspaceSecretV2 {
    readonly v: 2
    readonly type: 'workspace-secret'
    readonly blobId: string
    readonly readKey: string        // base64url 32 bytes
    readonly readerSeed: string     // base64url 32 bytes
    readonly writerSeed?: string    // base64url 32 bytes (omitted for read-only)
}

/**
 * Workspace Invite v2 — envelope for sharing.
 * Spec §4.2.
 */
export interface WorkspaceInviteV2 {
    readonly v: 2
    readonly type: 'workspace-invite'
    readonly apiBaseUrl: string
    readonly mode: 'read' | 'write'
    readonly inviteToken: string    // opaque server-signed grant
    readonly secret: string         // base64url(WorkspaceSecretV2 JSON)
    readonly title?: string
    readonly emoji?: string
    readonly path?: string
    readonly view?: string
    readonly app?: string
}

/**
 * Config Connection Token v2 — replaces legacy {b,r} format.
 * Spec §4.3.
 */
export interface ConfigConnectionTokenV2 {
    readonly v: 2
    readonly type: 'config-connection-token'
    readonly apiBaseUrl: string
    readonly secret: string         // base64url(WorkspaceSecretV2 JSON)
}

/**
 * Auth proof payload — signed by client for challenge-response.
 * Spec §4.4.
 */
export interface WorkspaceAuthProof {
    readonly v: 1
    readonly type: 'workspace-auth-proof'
    readonly challengeId: string
    readonly blobId: string
    readonly scope: 'read' | 'write'
    readonly subjectKind: 'reader' | 'writer'
    readonly subjectKid: string
}

// ── Derivation helpers ─────────────────────────────────────────

/**
 * Derive a kid (key identifier) from an Ed25519 public key.
 * kid = base64url(SHA-256(pk)[0..15])
 * This MUST match the C# side: CommitSignatureVerifier.DeriveKid / CryptoProvider.ComputeKid.
 */
export async function deriveKid(publicKeyBytes: Uint8Array): Promise<string> {
    const hash = await crypto.subtle.digest('SHA-256', publicKeyBytes as BufferSource)
    return base64UrlEncode(new Uint8Array(hash).slice(0, 16))
}

// ── Encoding helpers ───────────────────────────────────────────

export function base64UrlEncode(bytes: Uint8Array): string {
    let binary = ''
    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i])
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

export function base64UrlDecode(str: string): Uint8Array {
    let base64 = str.replace(/-/g, '+').replace(/_/g, '/')
    while (base64.length % 4) base64 += '='
    const binary = atob(base64)
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
    return bytes
}

export function canonicalJsonStringify(value: unknown): string {
    if (value === null) return 'null'
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        return JSON.stringify(value)
    }

    if (Array.isArray(value)) {
        return `[${value.map(item => canonicalJsonStringify(item)).join(',')}]`
    }

    if (typeof value !== 'object') {
        throw new TypeError('Unsupported value in canonical JSON serialization')
    }

    const entries = Object.entries(value as Record<string, unknown>)
        .filter(([, entryValue]) => entryValue !== undefined)
        .sort(([left], [right]) => left < right ? -1 : left > right ? 1 : 0)

    return `{${entries
        .map(([key, entryValue]) => `${JSON.stringify(key)}:${canonicalJsonStringify(entryValue)}`)
        .join(',')}}`
}

/**
 * Encode a WorkspaceSecretV2 to its serialized form:
 * base64url(UTF8(canonical_json(secret)))
 */
export function encodeWorkspaceSecretV2(secret: WorkspaceSecretV2): string {
    const json = canonicalJsonStringify(secret)
    const bytes = new TextEncoder().encode(json)
    return base64UrlEncode(bytes)
}

/**
 * Decode a serialized WorkspaceSecretV2.
 * Returns null if the input is not a valid v2 workspace secret.
 */
export function decodeWorkspaceSecretV2(encoded: string): WorkspaceSecretV2 | null {
    try {
        const bytes = base64UrlDecode(encoded)
        const json = new TextDecoder().decode(bytes)
        const parsed = JSON.parse(json)
        if (parsed.v !== 2 || parsed.type !== 'workspace-secret') return null
        if (!parsed.blobId || !parsed.readKey || !parsed.readerSeed) return null
        return parsed as WorkspaceSecretV2
    } catch {
        return null
    }
}

/**
 * Encode a WorkspaceInviteV2 to its URL form:
 * base64url(UTF8(canonical_json(invite)))
 */
export function encodeWorkspaceInviteV2(invite: WorkspaceInviteV2): string {
    const json = canonicalJsonStringify(invite)
    const bytes = new TextEncoder().encode(json)
    return base64UrlEncode(bytes)
}

/**
 * Decode a WorkspaceInviteV2 from its serialized form.
 * Returns null if input is not a valid v2 invite.
 */
export function decodeWorkspaceInviteV2(encoded: string): WorkspaceInviteV2 | null {
    try {
        const bytes = base64UrlDecode(encoded)
        const json = new TextDecoder().decode(bytes)
        const parsed = JSON.parse(json)
        if (parsed.v !== 2 || parsed.type !== 'workspace-invite') return null
        if (!parsed.apiBaseUrl || !parsed.mode || !parsed.inviteToken || !parsed.secret) return null
        return parsed as WorkspaceInviteV2
    } catch {
        return null
    }
}

/**
 * Encode a ConfigConnectionTokenV2.
 */
export function encodeConfigConnectionTokenV2(token: ConfigConnectionTokenV2): string {
    const json = canonicalJsonStringify(token)
    const bytes = new TextEncoder().encode(json)
    return base64UrlEncode(bytes)
}

/**
 * Decode a ConfigConnectionTokenV2.
 */
export function decodeConfigConnectionTokenV2(encoded: string): ConfigConnectionTokenV2 | null {
    try {
        const bytes = base64UrlDecode(encoded)
        const json = new TextDecoder().decode(bytes)
        const parsed = JSON.parse(json)
        if (parsed.v !== 2 || parsed.type !== 'config-connection-token') return null
        if (!parsed.apiBaseUrl || !parsed.secret) return null
        return parsed as ConfigConnectionTokenV2
    } catch {
        return null
    }
}

/**
 * Parse input that could be either a workspace invite URL fragment
 * or a clipboard-format invite string.
 *
 * Supports:
 *   #invite=<base64url(WorkspaceInviteV2)>
 *   sb-invite:<base64url(WorkspaceInviteV2)>
 *
 * Returns the decoded invite or null.
 */
export function parseInviteInput(input: string): WorkspaceInviteV2 | null {
    if (!input) return null

    // URL fragment format: #invite=...
    const hashMatch = input.match(/[#&]invite=([A-Za-z0-9_-]+)/)
    if (hashMatch) {
        return decodeWorkspaceInviteV2(hashMatch[1])
    }

    // Clipboard format: sb-invite:...
    const clipMatch = input.match(/^sb-invite:([A-Za-z0-9_-]+)$/)

    if (clipMatch) {
        return decodeWorkspaceInviteV2(clipMatch[1])
    }

    return null
}

// ── Auth transport types (Spec §4.4, §4.5, §6.1) ──────────────
// These define the contract for the JS SDK auth transport that
// the build phase will implement and wire into HttpRemoteStore
// and WsNotificationClient.

/**
 * A workspace access token obtained from the server after
 * challenge-signing. Opaque to the client; used as
 * `Authorization: Bearer <token>` on dataplane requests
 * and in WS subscribe payloads.
 */
export interface WorkspaceAccessToken {
    /** The opaque bearer token string. */
    readonly token: string
    /** ISO-8601 expiry time. */
    readonly expiresAt: string
    /** Which blobId this token is scoped to. */
    readonly blobId: string
    /** Token scope: 'read' or 'write'. */
    readonly scope: 'read' | 'write'
}

/**
 * Auth provider contract for the JS SDK transport layer.
 *
 * Spec §9.1: HttpRemoteStore and HttpStoreBackend MUST use an
 * auth-provider layer that fetches challenges, signs with
 * readerSeed/writerSeed, caches access tokens, and refreshes
 * on expiry — instead of sending workspace secrets as bearer tokens.
 *
 * Implementations:
 * - SessionTokenProvider (build phase): full challenge-signing flow
 * - StaticTokenProvider (tests): returns a fixed token
 */
export interface WorkspaceAuthProvider {
    /**
     * Get a valid workspace access token, refreshing if needed.
     * The returned token is guaranteed to have at least 30s of
     * remaining lifetime (or the provider will refresh proactively).
     */
    getAccessToken(scope: 'read' | 'write', signal?: AbortSignal): Promise<WorkspaceAccessToken>

    /**
     * Invalidate any cached tokens for the given scope.
     * Called after a 401 response to force re-authentication.
     */
    invalidateToken(scope: 'read' | 'write'): void

    /** The blobId this provider is scoped to. */
    readonly blobId: string
}
